import type { ChildProfile, DailyGoal } from '@oefy/contracts';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ChildAvatar } from '../ui/avatar';
import { Backdrop } from '../ui/backdrop';
import { BrandMark } from '../ui/brand';
import { cx } from '../ui/styles';
import { copy } from './copy';
import { GoalStars } from './goal-stars';
import { Worm } from './worm';
import type { WormAction, WormMood, WormPose } from './worm';

interface ChildShellProps {
  readonly backTo?: string;
  readonly backLabel?: string;
  readonly child?: Pick<ChildProfile, 'firstName' | 'avatar' | 'avatarColor' | 'photoUrl'> & {
    readonly schoolGroup: number;
  };
  readonly aside?: ReactNode;
  readonly goal?: { readonly childId: string; readonly goal: DailyGoal } | undefined;
  readonly wormMessage: string;
  readonly wormMood?: WormMood;
  readonly wormAction?: WormAction | undefined;
  readonly wormPose?: WormPose | undefined;
  readonly focus?: boolean;
  /** A game that fills the screen: no header, worm or page padding while it runs. */
  readonly fullscreen?: boolean;
  readonly children: ReactNode;
}

export function ChildShell({
  backTo,
  backLabel = copy.back,
  child,
  aside,
  goal,
  wormMessage,
  wormMood,
  wormAction,
  wormPose,
  focus = false,
  fullscreen = false,
  children,
}: ChildShellProps) {
  if (fullscreen) {
    return (
      <div className="fixed inset-0 h-dvh w-full overflow-hidden overscroll-none bg-[#d6edf2] font-child text-slate-900">
        {children}
      </div>
    );
  }
  return (
    <Backdrop className="font-child text-slate-900">
      <header className="relative mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-4 py-4">
        {backTo ? (
          <>
            <BrandMark size="lg" className="hidden sm:block" />
            <Link
              to={backTo}
              aria-label={backLabel}
              className="flex size-12 shrink-0 items-center justify-center rounded-full bg-white text-slate-700 shadow-warm ring-1 ring-slate-200 transition hover:-translate-x-0.5 hover:bg-slate-50 hover:text-slate-900"
            >
              <ArrowLeft aria-hidden="true" className="size-6" strokeWidth={2.5} />
            </Link>
          </>
        ) : (
          <Link to="/" aria-label={copy.home} className="flex items-center gap-2.5 rounded-xl">
            <BrandMark size="lg" />
            <span className="text-2xl font-bold tracking-tight">Oefy</span>
          </Link>
        )}
        {child ? (
          <div
            className={cx(
              'items-center gap-2 rounded-full bg-white py-1.5 pr-4 pl-1.5 shadow-warm ring-1 ring-slate-200',
              focus ? 'hidden sm:flex' : 'flex',
            )}
          >
            <ChildAvatar
              avatar={child.avatar}
              color={child.avatarColor}
              photoUrl={child.photoUrl}
              size="md"
            />
            <span className="text-lg font-bold">{child.firstName}</span>
            <span
              className={cx(
                'rounded-full bg-amber-100 px-2 py-0.5 text-sm font-bold whitespace-nowrap text-amber-900',
                aside ? 'hidden sm:inline' : null,
              )}
            >
              {copy.group(child.schoolGroup)}
            </span>
            {goal ? <GoalStars childId={goal.childId} goal={goal.goal} /> : null}
          </div>
        ) : null}
        <div className="ml-auto flex grow justify-end sm:grow-0">{aside}</div>
      </header>
      <main className="@container/stage relative mx-auto max-w-6xl px-4 pb-48 motion-safe:animate-page-in">
        {children}
      </main>
      <Worm
        message={wormMessage}
        action={wormAction}
        pose={wormPose}
        {...(wormMood ? { mood: wormMood } : {})}
      />
    </Backdrop>
  );
}
